import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  MessageCircle, 
  User, 
  Shield,
  Clock
} from 'lucide-react';
import { ChatMessage } from '../types';
import { SocketManager } from '../utils/socketManager';

interface ChatInterfaceProps {
  emergencyId: string;
  currentUserId: string;
  currentUserName: string;
  currentUserType: 'dispatcher' | 'responder';
  messages: ChatMessage[];
  onSendMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  className?: string;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  emergencyId,
  currentUserId,
  currentUserName,
  currentUserType,
  messages,
  onSendMessage,
  className = ''
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketManager = useRef<SocketManager | null>(null);

  useEffect(() => {
    socketManager.current = new SocketManager();
    
    socketManager.current.onChatMessage((message) => {
      // Handle incoming messages
      console.log('Received chat message:', message);
    });

    return () => {
      socketManager.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    const message: Omit<ChatMessage, 'id' | 'timestamp'> = {
      senderId: currentUserId,
      senderName: currentUserName,
      senderType: currentUserType,
      message: newMessage.trim(),
      emergencyId
    };

    onSendMessage(message);
    
    // Send via socket
    const fullMessage: ChatMessage = {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date()
    };
    
    socketManager.current?.sendChatMessage(fullMessage);
    
    setNewMessage('');
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getSenderIcon = (senderType: ChatMessage['senderType']) => {
    switch (senderType) {
      case 'dispatcher':
        return <Shield className="w-4 h-4 text-blue-600" />;
      case 'responder':
        return <User className="w-4 h-4 text-green-600" />;
      case 'system':
        return <MessageCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getMessageBubbleStyle = (senderId: string) => {
    const isOwnMessage = senderId === currentUserId;
    return isOwnMessage
      ? 'bg-red-600 text-white ml-auto'
      : 'bg-gray-100 text-gray-900 mr-auto';
  };

  return (
    <div className={`bg-white rounded-lg shadow-md flex flex-col h-96 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b bg-gray-50 rounded-t-lg">
        <MessageCircle className="w-5 h-5 text-red-600" />
        <h3 className="font-semibold text-gray-900">Emergency Chat</h3>
        <span className="text-sm text-gray-600">#{emergencyId.slice(-6)}</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex flex-col">
              {message.senderType === 'system' ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                    {getSenderIcon(message.senderType)}
                    <span>{message.message}</span>
                    <Clock className="w-3 h-3" />
                    <span>{formatTime(message.timestamp)}</span>
                  </div>
                </div>
              ) : (
                <div className={`flex ${message.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${getMessageBubbleStyle(message.senderId)}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {getSenderIcon(message.senderType)}
                      <span className="text-xs font-medium opacity-75">
                        {message.senderName}
                      </span>
                      <span className="text-xs opacity-50">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm">{message.message}</p>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t bg-gray-50 rounded-b-lg">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            maxLength={500}
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {newMessage.length}/500 characters
        </div>
      </form>
    </div>
  );
};